import React, { useState } from "react";
import styled, { withTheme } from "styled-components";
import Container from "storybook-components/Container";
import { BodySemiboldStyle } from "storybook-components/Typography";
import { useMediaQuery } from "react-responsive";
import HubCardList from "./HubCardList";
import DropdownChevron from "../images/dropdown-chevron.svg";

const StyledCardSection = styled.div`
  background-color: ${({ theme }) => theme.colors.green5};
  padding-bottom: 64px;
  min-height: 400px;
  position: relative;
`;

const StyledCardContainer = styled(Container)`
  position: relative;
  z-index: 2;
  top: 0;
  margin: auto;
  left: 0;
  right: 0;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  margin-bottom: 40px;
  padding: 0 16px;
  ${({ theme }) => theme.media.tabletUp`
    padding: 0;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-bottom: 64px;
  `};
`;

const ResourceFilterLabel = styled.label`
  margin: 8px 0;
  ${BodySemiboldStyle}
  color: ${({ theme }) => theme.colors.gray1};
  ${({ theme }) => theme.media.tabletUp`
    margin: auto 4px;      
  `};
`;

const ResourceFilterSelect = styled.select`
  margin: 8px 0;
  height: 46px;
  border-radius: 10px;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.white};
  appearance: none;
  background-image: url(${DropdownChevron});
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 8px 4px;
  &:focus {
    outline: none;
  }
  ${({ theme }) => theme.media.tabletUp`
    margin: auto 40px auto 16px;
  `};
`;

const HubCardSection = ({ links, theme }) => {
  const linksBySubject = [];
  const linksByType = [];
  const allLinkObject = { name: "All", count: 0 };

  const createLinksByType = (allLinks, startingArray, seedObject) => {
    startingArray.push(seedObject);
    allLinks.forEach(link => {
      // Check if linksByType already has current link's type
      const linkType = startingArray.filter(type => type.name === link.type);
      // And either add the current type, or increment the count of the extant one
      if (linkType.length === 0) {
        startingArray.push({ name: link.type, count: 1 });
      } else {
        linkType[0].count += 1;
      }
    });
  };

  const createLinksBySubject = (allLinks, startingArray, seedObject) => {
    startingArray.push(seedObject);
    allLinks.forEach(link => {
      // Run through link tags (aka subjects) since a link can have several
      link.tags.forEach(tag => {
        // Check if linksBySubject already has current link's subject
        const linkSubject = startingArray.filter(
          subject => subject.name === tag
        );
        // And either add the current subject, or increment the count of the extant one
        if (linkSubject.length === 0) {
          startingArray.push({ name: tag, count: 1 });
        } else {
          linkSubject[0].count += 1;
        }
      });
    });
  };

  createLinksBySubject(links, linksBySubject, allLinkObject);
  createLinksByType(links, linksByType, allLinkObject);

  const [resourceTypeFilter, setResourceTypeFilter] = useState("All");
  const [resourceTypeFilterActive, setResourceTypeFilterActive] = useState(
    false
  );
  const [resourceTagFilter, setResourceTagFilter] = useState("All");
  const [resourceTagFilterActive, setResourceTagFilterActive] = useState(true);
  const [filteredLinks, setFilteredLinks] = useState(links);

  const querySettings = { query: `(min-width: ${theme.sizes.tablet}px)` };
  const isDesktopView = useMediaQuery(querySettings);

  const filterLinksByType = linkType => {
    return linkType === "All"
      ? links
      : links.filter(link => link.type === linkType);
  };
  const filterLinksByTag = linkTag => {
    return linkTag === "All"
      ? links
      : links.filter(link => link.tags.includes(linkTag));
  };
  const updateResourceTypeFilter = currentFilter => {
    setResourceTagFilter("All");
    setResourceTagFilterActive(false);
    setResourceTypeFilter(currentFilter);
    setResourceTypeFilterActive(true);
    setFilteredLinks(filterLinksByType(currentFilter));
  };

  const updateResourceTagFilter = currentFilter => {
    setResourceTypeFilter("All");
    setResourceTypeFilterActive(false);
    setResourceTagFilter(currentFilter);
    setResourceTagFilterActive(true);
    setFilteredLinks(filterLinksByTag(currentFilter));
  };

  return (
    <StyledCardSection>
      <StyledCardContainer>
        <FilterContainer>
          <ResourceFilterLabel
            htmlFor="tag-select"
            active={resourceTagFilterActive}
          >
            Subject:
          </ResourceFilterLabel>
          <ResourceFilterSelect
            id="tag-select"
            type="dropdown"
            onChange={e => updateResourceTagFilter(e.target.value)}
            value={resourceTagFilter}
          >
            {linksBySubject.map(linkSubject => {
              return (
                <option
                  key={`tag ${linkSubject.name}`}
                  value={linkSubject.name}
                >
                  {linkSubject.name !== "All"
                    ? `${linkSubject.name} (${linkSubject.count})`
                    : linkSubject.name}
                </option>
              );
            })}
          </ResourceFilterSelect>
          <ResourceFilterLabel
            htmlFor="type-select"
            active={resourceTypeFilterActive}
          >
            Type:
          </ResourceFilterLabel>
          <ResourceFilterSelect
            id="type-select"
            type="dropdown"
            value={resourceTypeFilter}
            onChange={e => updateResourceTypeFilter(e.target.value)}
          >
            {linksByType.map(linkType => {
              return (
                <option key={`type ${linkType.name}`} value={linkType.name}>
                  {linkType.name !== "All"
                    ? `${linkType.name} (${linkType.count})`
                    : linkType.name}
                </option>
              );
            })}
          </ResourceFilterSelect>
        </FilterContainer>
        <HubCardList isDesktopView={isDesktopView} links={filteredLinks} />
      </StyledCardContainer>
    </StyledCardSection>
  );
};

export default withTheme(HubCardSection);
